import  { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box,  
  Typography, 
  Fade, 
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  PauseCircle as PauseCircleIcon,
} from "@mui/icons-material";
import JobCodeButtonGrid from './JobCodeButtonGrid';
import axios from "axios";
import PropTypes from 'prop-types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function IdleTimes(props) {
    const [jobCodes, setJobCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fadeIn, setFadeIn] = useState(false);
    
    const { 
        username, 
        jobNumber, 
        customer, 
        operation, 
        product,
        startedTime,
        start_datetime,
        employee_id,
        machine_code,
        operation_code,
        operation_description 
    } = props;
    
    const operationType = "Idle";

    const navigate = useNavigate();


    useEffect(() => {
        let redirectTimer;

        const fetchJobCodes = async () => {
            try {
                setLoading(true);
                setError('');
                const res = await axios.get(`${API_BASE_URL}/IdleTimes/${username}`);
                setJobCodes(res.data);
                
                // Trigger fade-in animation after data loads
                setTimeout(() => setFadeIn(true), 100);
            } catch (err) {
                console.error("Error fetching idle times:", err);

                // Auth-only redirect: only kick to login when session is
                // genuinely invalid. Other errors keep the existing Alert
                // so the parent JobOperation page (and any active job)
                // remain intact.
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    setError('Session expired. Redirecting to login...');
                    redirectTimer = setTimeout(() => {
                        navigate("/", { replace: true });
                    }, 2500);
                } else {
                    setError('Failed to load idle operations. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };
        
        if (username) {
            fetchJobCodes();
        }

        return () => {
            if (redirectTimer) clearTimeout(redirectTimer);
        };
    }, [username, navigate]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '260px',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress 
                    size={60} 
                    thickness={4}
                    sx={{ 
                        color: '#2196f3',
                        filter: 'drop-shadow(0 4px 8px rgba(33, 150, 243, 0.3))'
                    }} 
                />
                <Typography variant="body1" color="textSecondary" fontWeight={500}>
                    Loading idle operations...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '260px',
                p: 2
            }}>
                <Alert 
                    severity="error" 
                    sx={{ 
                        borderRadius: '12px',
                        maxWidth: 400
                    }}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Fade in={fadeIn} timeout={800}>
            
                        {jobCodes.length > 0 ? (
                            <Box sx={{ width: '100%'}}>
                                <JobCodeButtonGrid 
                                    jobCodes={jobCodes} 
                                    username={username} 
                                    jobNumber={jobNumber} 
                                    customer={customer} 
                                    operation={operation} 
                                    product={product} 
                                    operationType={operationType} 
                                    startedTime={startedTime} 
                                    employee_id={employee_id} 
                                    machine_code={machine_code} 
                                    start_datetime={start_datetime} 
                                    operation_code={operation_code} 
                                    operation_description={operation_description}
                                />
                                

                            </Box>
                        ) : (
                            <Box sx={{ 
                                textAlign: 'center',
                                p: 4
                            }}>
                                <PauseCircleIcon 
                                    sx={{ 
                                        fontSize: 64, 
                                        color: 'rgba(33, 150, 243, 0.3)',
                                        mb: 2
                                    }} 
                                />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    No Idle Operations Available
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    There are currently no idle or break operations configured for this job.
                                </Typography>
                            </Box>
                        )}
   
        </Fade>
    );
}

// Define PropTypes
IdleTimes.propTypes = {
    username: PropTypes.string.isRequired,
    jobNumber: PropTypes.string.isRequired,
    customer: PropTypes.string.isRequired,
    operation: PropTypes.string.isRequired,
    product: PropTypes.string.isRequired,
    startedTime: PropTypes.string.isRequired,
    start_datetime: PropTypes.string.isRequired,
    employee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    machine_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    operation_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    operation_description: PropTypes.string.isRequired,
};